import { Navigate, Outlet } from 'react-router-dom';
import { IndividualType } from 'store/individuals/individual';
import { User } from 'store/session/user';
import Authentication from 'views/authentication/Auth';
import ForgotPassword from 'views/authentication/forgot-password/ForgotPassword';
import Invite from 'views/authentication/invite/Invite';
import SignIn from 'views/authentication/sign-in/SignIn';
import SignUp from 'views/authentication/sign-up/SignUp';
import CargoJourneysDetails from 'views/cargo-journeys/CargoJourneysDetails';
import LegView from 'views/cargo-journeys/LegView';
import CargoManagement from 'views/cargo-management/CargoManagement';
import ProductTemplatesDashboard from 'views/custom-declaration/products/ProductTemplatesDashboard';
import HelpView, { DocumentationType } from 'views/documentation/Help';
import DriverManagement from 'views/drivers/management';
import ViewMap from 'views/drivers/view-map';
import InvoiceUpload from 'views/invoice-upload/InvoiceUpload';
import Jobs from 'views/jobs/Jobs';
import PrivacyPolicy from 'views/legal-info/PrivacyPolicy';
import TermsAndConditions from 'views/legal-info/TermsAndConditions';
import MobileLegDetailsView from 'views/mobile/legs/MobileLegDetailsView';
import MobileLegMapView from 'views/mobile/legs/MobileLegMapView';
import MobileLegView from 'views/mobile/legs/MobileLegView';
import MobileWeighingView from 'views/mobile/weighings/MobileWeighingView';
import ChangePassword from 'views/settings/components/ChangePassword';
import CompanyDetails from 'views/settings/components/company-details/CompanyDetails';
import DigitalCertificate from 'views/settings/components/digital-certificates/DigitalCertificate';
import Hmrc from 'views/settings/components/Hmrc';
import HmrcConfirmation from 'views/settings/components/HmrcConfirmation';
import PersonalInformation from 'views/settings/components/PersonalInformation';
import Settings from 'views/settings/Settings';
import UserManagement from 'views/user-management/UserManagement';
import VehiclesManagement from 'views/vehicles/VehiclesManagement';
import MobileSkeleton from '../../views/mobile/skeleton/MobileSkeleton';
import setupRoutes from './setup/setup.routes';
import eDocketRoutes from './edocket/edocket.routes';
import Skeleton from './skeleton/Skeleton';
import AddEditProductTemplate from '../../views/custom-declaration/products/AddEditProductTemplate';
import AddEditItemFieldTemplate from '../../views/templates/item-field-templates/AddEditItemFieldTemplate';
import TemplateDashboard from 'views/templates/TemplatesDashboard';
import DashboardView from '../../views/dashboard/DashboardView';
import DeclarationDashboard from '../../views/custom-declaration/DeclarationDashboard';
import ArrivalNotificationsDashboard from '../../views/ics2-arrival-notifications/ArrivalNotificationsDashboard';
import ArrivalNotificationPage from '../../views/ics2-arrival-notifications/ArrivalNotificationPage';
import ArrivalNotificationFormTab from '../../views/ics2-arrival-notifications/ArrivalNotificationFormTab';
import ArrivalNotificationHistory from '../../views/ics2-arrival-notifications/ArrivalNotificationHistory';
import ArrivalNotificationHistoryTable from '../../views/ics2-arrival-notifications/ArrivalNotificationHistoryTable';
import { DeclarationCountry } from '../../store/declarations/enums/common/declaration-country';
import { DeclarationInternalType } from '../../store/declarations/enums/common/declaration-internal-type';
import { Declaration } from '../../store/declarations/declaration';
import AccessDeniedMessage from './AccessDeniedMessage';
import CustomsExciseSettings from '../../views/settings/components/CustomsExciseSettings';
import filesRoutes from './files/files.routes';
import AuthenticationSignIn from 'views/authentication/OAuthSignIn';
import CreateShipmentPage from 'views/shipments/create-shipment/CreateShipmentPage';
import ManifestsDashboard from 'views/manifests/dashboard/ManifestsDashboard';
import CreateManifestPage from 'views/manifests/create-manifest/CreateManifestPage';
import CreateManifestJob from 'views/manifests/job/create-job/CreateManifestJob';
import ManifestDetails from 'views/manifests/manifest-details/ManifestDetails';
import ManifestDocuments from 'views/manifests/manifest-details/tabs/ManifestDocuments';
import ManifestNotes from 'views/manifests/manifest-details/tabs/ManifestNotes';
import ManifestHistory from 'views/manifests/manifest-details/tabs/ManifestHistory';
import ManifestOverview from 'views/manifests/manifest-details/tabs/ManifestOverview';
import ManifestEns from 'views/manifests/manifest-details/tabs/ManifestEns';
import { ManifestTabKeys } from 'views/manifests/manifest-details/types';
import TeamsPage from 'views/teams/teams-dashboard/TeamsPage';
import TeamPage from 'views/teams/team-page/TeamPage';
import { TeamPageTabKeys } from 'views/teams/types';
import MembersTab from 'views/teams/team-page/tabs/members/MembersTab';
import ManifestsTab from 'views/teams/team-page/tabs/manifests/ManifestsTab';
import JobsTab from 'views/teams/team-page/tabs/jobs/JobsTab';
import PartiesTab from 'views/teams/team-page/tabs/parties/PartiesTab';
import DetailsTab from 'views/teams/team-page/tabs/details/DetailsTab';
import {
    COMPANY_DETAILS_ACCESS_ROLES,
    DECLARATIONS_ACCESS_ROLES,
    INVOICES_ACCESS_ROLES,
    JOBS_ACCESS_ROLES,
    MANIFESTS_ACCESS_ROLES,
    SHIPMENTS_ACCESS_ROLES,
    TEAMS_ACCESS_ROLES,
    TEMPLATES_ACCESS_ROLES,
    USER_MANAGEMENT_ACCESS_ROLES,
} from 'config/roles/roles';
import CreateJobPage from 'views/jobs/CreateJobPage';
import JobDetailsPage from 'views/jobs/JobDetailsPage';
import ManifestJobDetails from 'views/manifests/job/job-details/ManifestJobDetails';
import InvoicesPage from 'views/invoices/dashboard/InvoicesPage';
import TemplateView from 'views/templates/TemplateView';
import TemplateEdit from 'views/templates/TemplateEdit';
import InvoiceDetails from 'views/invoices/invoice-details/InvoiceDetails';
import InvoiceContextProvider from 'views/invoices/invoice-details/InvoiceContextProvider';
import { InvoiceDetailsTabs } from 'views/invoices/types';
import InvoiceOverview from 'views/invoices/invoice-details/tabs/OverviewTab';
import InvoiceHistory from 'views/invoices/invoice-details/tabs/HistoryTab';
import InvoiceConfigPage from 'views/invoices/settings/InvoiceConfigPage';
import OverviewTab from 'views/jobs/tabs/OverviewTab';
import { JobTabKeys } from 'views/jobs/types';
import DocumentsTab from 'views/jobs/tabs/DocumentsTab';
import NotesTab from 'views/jobs/tabs/NotesTab';
import HistoryTab from 'views/jobs/tabs/HistoryTab';
import JobInvoicesTab from 'views/jobs/tabs/InvoicesTab';
import TeamPageInvoicesTab from 'views/teams/team-page/tabs/invoices/InvoicesTab';
import { ManifestJobTabKeys } from 'views/manifests/job/job-details/types';
import GeneralSettings from 'views/invoices/settings/tabs/GeneralSettings';
import AdditionalCharges from 'views/invoices/settings/tabs/AdditionalCharges';
import { InvoiceConfigTabKeys } from 'views/invoices/settings/getActiveTabKey';
import PartyPage from 'views/customers/customer/PartyPage';
import CustomerOverviewTab from 'views/customers/customer/tabs/overview/OverviewTab';
import CustomerFilesTab from 'views/customers/customer/tabs/files/CustomerFilesTab';
import CustomerJobsTab from 'views/customers/customer/tabs/jobs/JobsTab';
import CustomerInvoicesTab from 'views/customers/customer/tabs/invoices/InvoicesTab';
import AdditionalChargesTab from 'views/customers/customer/tabs/additional-charges/AdditionalChargesTab';
import { CustomerDetailsTabs } from 'views/customers/customer/utils';
import CustomersPage from 'views/customers/CustomersPage';
import DeclarationPage from 'views/declarations/DeclarationPage';
import { ProtectedRoute, Route } from './ProtectedRoute';
import { getDeclarationChildren } from './utils';
import { AccessDeniedRoute } from './AccessDeniedRoute';
import MirroringPreparationPage from 'views/declarations/mirroring/preparation-page/MirroringPreparationPage';
import CommodityConditionsCenterPage from 'views/commodity-conditions-center/CommodityConditionsCenterPage';
import AddEditCommodityCondition from 'views/commodity-conditions-center/AddEditCommodityCondition';

const routesConfig = (user: User | undefined, declaration: Declaration | undefined): Route[] => {
    return [
        { path: '/access-denied', name: 'Access Denied', element: <AccessDeniedMessage /> },
        { path: '/authenticating', name: 'AuthenticationSignIn', element: <AuthenticationSignIn /> },
        {
            path: '/',
            name: 'Authentication',
            element: <Authentication />,
            children: [
                {
                    path: '',
                    name: 'SignIn',
                    element: <SignIn />,
                    index: true,
                },
                {
                    path: 'signUp',
                    name: 'SignUp',
                    element: <SignUp />,
                },
                {
                    path: 'reset',
                    name: 'ResetPassword',
                    children: [
                        {
                            path: ':token',
                            name: 'ResetPasswordToken',
                            element: <ForgotPassword />,
                        },
                        {
                            path: '',
                            name: 'ResetPassword',
                            element: <ForgotPassword />,
                            index: true,
                        },
                    ],
                },
            ],
        },
        {
            path: '/invite/:token',
            name: 'Invite',
            element: <Invite />,
        },
        {
            path: '/terms-and-conditions',
            name: 'TermsAndConditions',
            element: <TermsAndConditions />,
        },
        {
            path: '/privacy-policy',
            name: 'Invite',
            element: <PrivacyPolicy />,
        },
        {
            name: 'Skeleton',
            element: (
                <ProtectedRoute user={user}>
                    <Skeleton />
                </ProtectedRoute>
            ),
            children: [
                {
                    path: 'home',
                    name: 'HomePage',
                    element: <DashboardView />,
                    index: true,
                },
                {
                    path: 'customs-declarations/:country/:internalType/products/:type',
                    name: 'Products',
                    element: (
                        <ProtectedRoute user={user} allowedUsers={DECLARATIONS_ACCESS_ROLES}>
                            <ProductTemplatesDashboard />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'customs-declarations/:country/:internalType/products/:type/new',
                    name: 'Add Product',
                    element: (
                        <ProtectedRoute user={user} allowedUsers={DECLARATIONS_ACCESS_ROLES}>
                            <AddEditProductTemplate />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'customs-declarations/:country/:internalType/products/:type/:id',
                    name: 'Edit Product',
                    element: (
                        <ProtectedRoute user={user} allowedUsers={DECLARATIONS_ACCESS_ROLES}>
                            <AddEditProductTemplate />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'customs-declarations/:country/:internalType/item-field-templates/:type/new',
                    name: 'Add Item Field Template',
                    element: (
                        <ProtectedRoute user={user} allowedUsers={DECLARATIONS_ACCESS_ROLES}>
                            <AddEditItemFieldTemplate />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'customs-declarations/:country/:internalType/item-field-templates/:type/:id',
                    name: 'Edit Item Field Template',
                    element: (
                        <ProtectedRoute user={user} allowedUsers={DECLARATIONS_ACCESS_ROLES}>
                            <AddEditItemFieldTemplate />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: `customs-declarations/${DeclarationCountry.EU}/${DeclarationInternalType.ARRIVAL_NOTIFICATION_ICS2}`,
                    name: 'Arrival Notifications (ICS2)',
                    element: (
                        <ProtectedRoute user={user} allowedUsers={DECLARATIONS_ACCESS_ROLES}>
                            <ArrivalNotificationsDashboard />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'arrival-notifications/:arrivalNotificationId',
                    name: 'Arrival Notifications (ICS2)',
                    element: (
                        <ProtectedRoute user={user} allowedUsers={DECLARATIONS_ACCESS_ROLES}>
                            <ArrivalNotificationPage />
                        </ProtectedRoute>
                    ),
                    children: [
                        { path: '', name: 'Arrival Notification', element: <ArrivalNotificationFormTab /> },
                        {
                            path: 'history',
                            name: 'History',
                            element: <ArrivalNotificationHistory />,
                            children: [
                                {
                                    path: '*',
                                    name: 'History Table',
                                    element: <ArrivalNotificationHistoryTable />,
                                    index: true,
                                },
                            ],
                        },
                    ],
                },
                {
                    path: 'customs-declarations/:country/:type',
                    name: 'Customs declaration',
                    element: (
                        <ProtectedRoute user={user} allowedUsers={DECLARATIONS_ACCESS_ROLES}>
                            <DeclarationDashboard />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'invoice-upload/:country/:type/job/:jobId',
                    name: 'Customs declaration',
                    element: (
                        <ProtectedRoute user={user} allowedUsers={DECLARATIONS_ACCESS_ROLES}>
                            <InvoiceUpload />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'invoice-upload/:country/:type/',
                    name: 'Customs declaration',
                    element: (
                        <ProtectedRoute user={user} allowedUsers={DECLARATIONS_ACCESS_ROLES}>
                            <InvoiceUpload />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'drivers/view-map',
                    name: 'View Map',
                    element: <ViewMap />,
                },
                {
                    path: 'drivers/management',
                    name: 'Drivers Management',
                    element: <DriverManagement />,
                },
                {
                    path: 'login',
                    name: 'LoginPage',
                    element: <SignIn />,
                },
                {
                    path: 'declarations/:declarationId',
                    name: 'Declaration Page',
                    element: (
                        <ProtectedRoute user={user} allowedUsers={DECLARATIONS_ACCESS_ROLES}>
                            <DeclarationPage>
                                <Outlet />
                            </DeclarationPage>
                        </ProtectedRoute>
                    ),
                    children: getDeclarationChildren(declaration),
                },
                {
                    path: 'declarations/:declarationId/prepare-mirroring',
                    name: 'Mirroring Preparation',
                    element: (
                        <ProtectedRoute user={user} allowedUsers={DECLARATIONS_ACCESS_ROLES}>
                            <MirroringPreparationPage />
                        </ProtectedRoute>
                    ),
                },
                // TODO:
                {
                    path: 'commodity-conditions-center',
                    name: 'Commodity Conditions Center',
                    element: (
                        <ProtectedRoute user={user}>
                            <CommodityConditionsCenterPage />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'commodity-conditions-center/new',
                    name: 'New Commodity Condition',
                    element: (
                        <ProtectedRoute user={user}>
                            <AddEditCommodityCondition />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'commodity-conditions-center/:id',
                    name: 'Commodity Condition',
                    element: (
                        <ProtectedRoute user={user}>
                            <AddEditCommodityCondition />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'settings/hmrc/confirmation',
                    name: 'Settings',
                    element: <HmrcConfirmation />,
                },
                {
                    path: 'settings',
                    name: 'Settings',
                    element: <Settings />,
                    children: [
                        {
                            path: 'personal-information',
                            name: 'PersonalInformation',
                            element: <PersonalInformation />,
                            index: true,
                        },
                        {
                            path: 'company-details',
                            name: 'CompanyDetails',
                            element: (
                                <ProtectedRoute user={user} allowedUsers={COMPANY_DETAILS_ACCESS_ROLES}>
                                    <CompanyDetails />
                                </ProtectedRoute>
                            ),
                        },
                        {
                            path: 'user-management',
                            name: 'UserManagement',
                            element: (
                                <ProtectedRoute user={user} allowedUsers={USER_MANAGEMENT_ACCESS_ROLES}>
                                    <UserManagement />
                                </ProtectedRoute>
                            ),
                        },
                        {
                            path: 'digital-certificate',
                            name: 'DigitalCertificate',
                            element: <DigitalCertificate />,
                        },
                        {
                            path: 'change-password',
                            name: 'ChangePassword',
                            element: <ChangePassword />,
                        },
                        {
                            path: 'hmrc',
                            name: 'HMRC',
                            element: <Hmrc />,
                        },
                        {
                            path: 'customs-and-excise',
                            name: 'Customs & Excise Settings',
                            element: <CustomsExciseSettings />,
                        },
                    ],
                },
                {
                    path: 'customers',
                    name: 'Customers',
                    element: <CustomersPage />,
                },
                {
                    path: 'customers/:customerId',
                    name: 'Customers Details',
                    element: (
                        <PartyPage>
                            <Outlet />
                        </PartyPage>
                    ),
                    children: [
                        {
                            index: true,
                            name: 'Overview tab',
                            element: <CustomerOverviewTab />,
                        },
                        {
                            path: CustomerDetailsTabs.FILES,
                            name: 'Customer Files tab',
                            element: <CustomerFilesTab />,
                        },
                        {
                            path: CustomerDetailsTabs.ADDITIONAL_CHARGES,
                            name: 'Additional Charges tab',
                            element: <AdditionalChargesTab />,
                        },
                        {
                            path: CustomerDetailsTabs.JOBS,
                            name: 'Jobs tab',
                            element: <CustomerJobsTab />,
                        },
                        {
                            path: CustomerDetailsTabs.INVOICES,
                            name: 'Invoices tab',
                            element: <CustomerInvoicesTab />,
                        },
                        { path: '*', name: 'Guard', element: <Navigate to="." replace /> },
                    ],
                },
                {
                    path: 'teams',
                    name: 'Teams',
                    element: (
                        <AccessDeniedRoute condition={Boolean(user?.role) && TEAMS_ACCESS_ROLES.includes(user?.role!)}>
                            <Outlet />
                        </AccessDeniedRoute>
                    ),
                    children: [
                        {
                            index: true,
                            name: 'Teams Page',
                            element: <TeamsPage />,
                        },
                        {
                            path: ':teamId',
                            name: 'Team Page',
                            element: (
                                <TeamPage>
                                    <Outlet />
                                </TeamPage>
                            ),
                            children: [
                                {
                                    index: true,
                                    name: 'Manifests tab',
                                    element: <ManifestsTab />,
                                },
                                {
                                    path: TeamPageTabKeys.JOBS,
                                    name: 'Jobs tab',
                                    element: <JobsTab />,
                                },
                                {
                                    path: TeamPageTabKeys.PARTIES,
                                    name: 'Parties tab',
                                    element: <PartiesTab />,
                                },
                                {
                                    path: TeamPageTabKeys.INVOICES,
                                    name: 'Invoices tab',
                                    element: <TeamPageInvoicesTab />,
                                },
                                {
                                    path: TeamPageTabKeys.DETAILS,
                                    name: 'Details tab',
                                    element: <DetailsTab />,
                                },
                                {
                                    path: TeamPageTabKeys.MEMBERS,
                                    name: 'Members tab',
                                    element: <MembersTab />,
                                },
                            ],
                        },
                    ],
                },
                // Commented out as part of work item #4269 | UI General | hide sections that are not working
                // ...weighbridgesRoutes(user),
                // {
                //     path: 'weighbridges/create',
                //     name: 'Weighbridges-create',
                //     element: (
                //         <ProtectedRoute
                //             user={user}
                //             allowedUsers={[
                //                 IndividualType.SUPER_ADMIN,
                //                 IndividualType.BROKER_ADMIN,
                //                 IndividualType.HAULIER_ADMIN,
                //                 IndividualType.SYSTEM_ADMIN,
                //                 IndividualType.TRADER_ADMIN,
                //             ]}
                //         >
                //             <WeighbridgesDashboard />
                //         </ProtectedRoute>
                //     ),
                // },
                {
                    path: 'templates',
                    name: 'templates',
                    element: (
                        <ProtectedRoute user={user} allowedUsers={TEMPLATES_ACCESS_ROLES}>
                            <TemplateView />
                        </ProtectedRoute>
                    ),
                    children: [
                        {
                            path: '',
                            name: 'dashboard',
                            element: <TemplateDashboard />,
                        },
                        {
                            path: 'add',
                            name: 'template-add',
                            element: <TemplateEdit />,
                        },
                        {
                            path: 'edit/:templateId',
                            name: 'template-edit',
                            element: <TemplateEdit />,
                        },
                    ],
                },

                // Commented out as part of work item #4269 | UI General | hide sections that are not working
                // {
                //     path: 'user-management/new',
                //     name: 'WCOUsersCreate',
                //     element: <WCOUsers />,
                // },
                // {
                //     path: 'user-management/:userId/edit',
                //     name: 'WCOUsersEdit',
                //     element: <WCOUsers />,
                // },
                {
                    path: 'jobs',
                    name: 'Jobs',
                    element: (
                        <ProtectedRoute user={user} allowedUsers={JOBS_ACCESS_ROLES.READ}>
                            <Jobs />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'jobs/:jobId',
                    name: 'JobsDetails',
                    element: (
                        <ProtectedRoute user={user} allowedUsers={JOBS_ACCESS_ROLES.READ}>
                            <JobDetailsPage>
                                <Outlet />
                            </JobDetailsPage>
                        </ProtectedRoute>
                    ),
                    children: [
                        {
                            index: true,
                            name: 'Overview',
                            element: <OverviewTab />,
                        },
                        {
                            path: JobTabKeys.DOCUMENTS,
                            name: 'Documents',
                            element: <DocumentsTab />,
                        },
                        {
                            path: JobTabKeys.NOTES,
                            name: 'Notes',
                            element: <NotesTab />,
                        },
                        {
                            path: JobTabKeys.INVOICES,
                            name: 'Invoices',
                            element: <JobInvoicesTab />,
                        },
                        {
                            path: JobTabKeys.HISTORY,
                            name: 'History',
                            element: <HistoryTab />,
                        },
                    ],
                },
                {
                    path: 'jobs/new',
                    name: 'CreateJobPage',
                    element: (
                        <ProtectedRoute user={user} allowedUsers={JOBS_ACCESS_ROLES.CREATE}>
                            <CreateJobPage />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'invoices',
                    name: 'InvoicesPage',
                    element: (
                        <ProtectedRoute user={user} allowedUsers={INVOICES_ACCESS_ROLES.DASHBOARD}>
                            <InvoicesPage />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'invoices/:invoiceId',
                    name: 'InvoiceDetails',
                    element: (
                        <ProtectedRoute user={user} allowedUsers={INVOICES_ACCESS_ROLES.SINGLE}>
                            <InvoiceContextProvider>
                                <InvoiceDetails>
                                    <Outlet />
                                </InvoiceDetails>
                            </InvoiceContextProvider>
                        </ProtectedRoute>
                    ),
                    children: [
                        {
                            index: true,
                            name: InvoiceDetailsTabs.OVERVIEW,
                            element: <InvoiceOverview />,
                        },
                        {
                            // Redirect from /overview if the user lands on the page
                            name: 'Overview',
                            path: InvoiceDetailsTabs.OVERVIEW,
                            element: <Navigate to=".." replace />,
                        },
                        {
                            path: InvoiceDetailsTabs.HISTORY,
                            name: InvoiceDetailsTabs.HISTORY,
                            element: <InvoiceHistory />,
                        },
                    ],
                },
                {
                    path: 'invoices/config',
                    name: 'InvoicesSettingsPage',
                    element: (
                        <ProtectedRoute user={user} allowedUsers={INVOICES_ACCESS_ROLES.CONFIG}>
                            <InvoiceConfigPage>
                                <Outlet />
                            </InvoiceConfigPage>
                        </ProtectedRoute>
                    ),
                    children: [
                        {
                            index: true,
                            name: 'General Settings',
                            element: <GeneralSettings />,
                        },
                        {
                            name: 'Additional Charges',
                            path: InvoiceConfigTabKeys.ADDITIONAL_CHARGES,
                            element: <AdditionalCharges />,
                        },
                    ],
                },
                {
                    path: 'cargo-management',
                    name: 'CargoManagement',
                    element: (
                        <ProtectedRoute
                            user={user}
                            allowedUsers={[
                                IndividualType.SUPER_ADMIN,
                                IndividualType.BROKER_ADMIN,
                                IndividualType.BROKER_CLERK,
                                IndividualType.SYSTEM_ADMIN,
                                IndividualType.TRADER_ADMIN,
                                IndividualType.TRADER_USER,
                            ]}
                        >
                            <CargoManagement />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'vehicles/management',
                    name: 'Vehicles',
                    element: (
                        <ProtectedRoute
                            user={user}
                            allowedUsers={[
                                IndividualType.SUPER_ADMIN,
                                IndividualType.BROKER_ADMIN,
                                IndividualType.BROKER_CLERK,
                                IndividualType.SYSTEM_ADMIN,
                                IndividualType.TRADER_ADMIN,
                                IndividualType.DRIVER,
                                IndividualType.HAULIER_ADMIN,
                                IndividualType.TRADER_USER,
                            ]}
                        >
                            <VehiclesManagement />
                        </ProtectedRoute>
                    ),
                },
                // Note: Hiding this part until it is complete
                // {
                //     path: 'shipments',
                //     name: 'Shipments',
                //     element: (
                //         <ProtectedRoute user={user} allowedUsers={SHIPMENTS_ACCESS_ROLES}>
                //             <ShipmentsPage />
                //         </ProtectedRoute>
                //     ),
                // },
                {
                    path: 'shipments/new',
                    name: 'Create shipment',
                    element: (
                        <ProtectedRoute user={user} allowedUsers={SHIPMENTS_ACCESS_ROLES}>
                            <CreateShipmentPage />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'manifests',
                    name: 'Manifests',
                    element: (
                        <ProtectedRoute user={user} allowedUsers={MANIFESTS_ACCESS_ROLES.READ}>
                            <Outlet />
                        </ProtectedRoute>
                    ),
                    children: [
                        {
                            index: true,
                            name: 'Manifests Dashboard',
                            element: <ManifestsDashboard />,
                        },
                        {
                            path: 'new',
                            name: 'Create Manifest',
                            element: (
                                <ProtectedRoute user={user} allowedUsers={MANIFESTS_ACCESS_ROLES.CREATE}>
                                    <CreateManifestPage />
                                </ProtectedRoute>
                            ),
                        },
                        {
                            path: ':manifestId',
                            name: 'Manifest Details',
                            element: (
                                <ManifestDetails>
                                    <Outlet />
                                </ManifestDetails>
                            ),
                            children: [
                                {
                                    index: true,
                                    name: 'Overview',
                                    element: <ManifestOverview />,
                                },
                                {
                                    path: ManifestTabKeys.ENS,
                                    name: 'ENS',
                                    element: <ManifestEns />,
                                },
                                {
                                    path: ManifestTabKeys.DOCUMENTS,
                                    name: 'Documents',
                                    element: <ManifestDocuments />,
                                },
                                {
                                    path: ManifestTabKeys.NOTES,
                                    name: 'Notes',
                                    element: <ManifestNotes />,
                                },
                                {
                                    path: ManifestTabKeys.HISTORY,
                                    name: 'History',
                                    element: <ManifestHistory />,
                                },
                            ],
                        },
                        {
                            path: ':manifestId/create-job',
                            name: 'Create Manifest Job',
                            element: <CreateManifestJob />,
                        },
                        {
                            path: ':manifestId/:jobId',
                            name: 'Manifest Job Tab View',
                            element: (
                                <ManifestJobDetails>
                                    <Outlet />
                                </ManifestJobDetails>
                            ),
                            children: [
                                {
                                    index: true,
                                    name: 'Overview',
                                    element: <OverviewTab />,
                                },
                                {
                                    path: ManifestJobTabKeys.DOCUMENTS,
                                    name: 'Documents',
                                    element: <DocumentsTab />,
                                },
                                {
                                    path: ManifestJobTabKeys.NOTES,
                                    name: 'Notes',
                                    element: <NotesTab />,
                                },
                                {
                                    path: ManifestJobTabKeys.INVOICES,
                                    name: 'Invoices',
                                    element: <JobInvoicesTab />,
                                },
                                {
                                    path: ManifestJobTabKeys.HISTORY,
                                    name: 'History',
                                    element: <HistoryTab />,
                                },
                            ],
                        },
                        {
                            path: ':manifestId/:jobId/create-declaration/:country/:type',
                            name: 'Create Manifest Job Declaration',
                            element: (
                                <ProtectedRoute user={user} allowedUsers={DECLARATIONS_ACCESS_ROLES}>
                                    <InvoiceUpload />
                                </ProtectedRoute>
                            ),
                        },
                        {
                            path: ':manifestId/:jobId/:declarationId',
                            name: 'Manifest Declaration Page',
                            element: (
                                <ProtectedRoute user={user} allowedUsers={MANIFESTS_ACCESS_ROLES.READ}>
                                    <DeclarationPage>
                                        <Outlet />
                                    </DeclarationPage>
                                </ProtectedRoute>
                            ),
                            children: getDeclarationChildren(declaration),
                        },
                        {
                            path: ':manifestId/:jobId/:declarationId/prepare-mirroring',
                            name: 'Mirroring Preparation',
                            element: (
                                <ProtectedRoute user={user} allowedUsers={DECLARATIONS_ACCESS_ROLES}>
                                    <MirroringPreparationPage />
                                </ProtectedRoute>
                            ),
                        },
                    ],
                },
                {
                    path: 'cargo-journeys/:id',
                    name: 'Cargo Joruney',
                    element: (
                        <ProtectedRoute
                            user={user}
                            allowedUsers={[
                                IndividualType.SUPER_ADMIN,
                                IndividualType.BROKER_ADMIN,
                                IndividualType.BROKER_CLERK,
                                IndividualType.SYSTEM_ADMIN,
                                IndividualType.TRADER_ADMIN,
                                IndividualType.DRIVER,
                                IndividualType.HAULIER_ADMIN,
                                IndividualType.TRADER_USER,
                            ]}
                        >
                            <CargoJourneysDetails />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'cargo-journeys/:journeyId/legs/:legId',
                    name: 'Leg',
                    element: (
                        <ProtectedRoute
                            user={user}
                            allowedUsers={[
                                IndividualType.SUPER_ADMIN,
                                IndividualType.BROKER_ADMIN,
                                IndividualType.BROKER_CLERK,
                                IndividualType.SYSTEM_ADMIN,
                                IndividualType.TRADER_ADMIN,
                                IndividualType.DRIVER,
                                IndividualType.HAULIER_ADMIN,
                                IndividualType.TRADER_USER,
                            ]}
                        >
                            <LegView />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'help',
                    name: 'Help',
                    element: <HelpView type={DocumentationType.AIS} />,
                },
                ...eDocketRoutes,
                ...setupRoutes,
                ...filesRoutes,
            ],
        },
        {
            path: '/mobile',
            name: 'MobileSkeleton',
            element: (
                <ProtectedRoute user={user}>
                    <MobileSkeleton />
                </ProtectedRoute>
            ),
            children: [
                {
                    name: 'MobileHomePageIndex',
                    element: <DashboardView />,
                    index: true,
                },
                {
                    path: 'home',
                    name: 'MobileHomePage',
                    element: <DashboardView />,
                },
                {
                    path: 'legs',
                    name: 'MobileLegs',
                    children: [
                        {
                            path: ':legId',
                            name: 'MobileLegView',
                            element: <MobileLegView />,
                            children: [
                                {
                                    name: 'MobileLegDetailsViewIndex',
                                    element: <MobileLegDetailsView />,
                                    index: true,
                                },
                                {
                                    path: 'details',
                                    name: 'MobileLegDetailsView',
                                    element: <MobileLegDetailsView />,
                                },
                                {
                                    path: 'map',
                                    name: 'MobileLegMapView',
                                    element: <MobileLegMapView />,
                                },
                            ],
                        },
                        // TODO remove this?
                        {
                            path: '',
                            name: 'MobileLegsView',
                            element: <Navigate to={'/mobile/home'} />,
                            index: true,
                        },
                    ],
                },
                {
                    path: 'weighings',
                    name: 'MobileWeighings',
                    children: [
                        {
                            path: ':weighingId',
                            name: 'MobileWeighingView',
                            element: <MobileWeighingView />,
                        },
                        // TODO remove this?
                        {
                            path: '',
                            name: 'MobileWeighingsView',
                            element: <Navigate to={'/mobile/home'} />,
                            index: true,
                        },
                    ],
                },
            ],
        },
    ];
};
export default routesConfig;
